<template>
  <div class="container-fluid pt-2">


    <div v-if="varianceReport">

      <div class="py-2 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mx-1"
          @click="toggleVarianceView"
        >
          {{ showAllItems ? 'Only With Issue' : 'All Listings' }}
        </button>

        <button
          type="button"
          class="btn btn-sm btn-primary mx-1"
          @click="generatePDF"
        >
          Download PDF
        </button>

        <button
          v-if="canAdjustStock && varianceReport && !varianceReport.applied_at"
          type="button"
          class="btn btn-sm btn-danger mx-1"
          :disabled="applying"
          @click="adoptAsInventory"
        >
          <span v-if="applying" class="spinner-border spinner-border-sm mr-1"></span>Update Inventory
        </button>
        <!-- Applied once: stock was set to these counts then; applying again would undo later sales/restocks. -->
        <button v-else-if="varianceReport && varianceReport.applied_at" type="button" class="btn btn-sm btn-outline-secondary mx-1" disabled>
          <i class="bi bi-check2-circle"></i> Inventory updated {{ formatDate(varianceReport.applied_at, 'DD MMM YYYY, HH:mm') }}
        </button>
      </div>

      <div ref="varianceReportPdf">
        <h3 class="text-primary">Variance Report</h3>
        <hr />

        <h5>
          Done By: {{ varianceReport.done_by?.name || 'System' }}
        </h5>

        <h6>
          Date: {{ formatDate(varianceReport.created_at, 'h:mm a DD-MMM-YYYY') }}
        </h6>
        <div class="pt-3">
              <a v-if="showAmountVariance" @click="showAmountVariance = false" class="btn btn-sm btn-outline-primary">Hide Amount Variance</a>
        <a v-else @click="showAmountVariance=true" class="btn btn-sm btn-outline-primary">Show Amount Variance</a>
        </div>


        <table class="table table-sm table-striped mt-3">
          <thead>
            <tr>
              <th>#</th>
              <th>Item</th>
              <th>Dukakit Count</th>
              <th>Physical Count</th>
              <th>Variance</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(item, index) in displayedStock"
              :key="item.id || index"
            >
              <td>{{ index + 1 }}</td>
              <td>{{ item.product || 'N/A' }}</td>

              <td :class="varianceClass(item)">
                {{ formatQty(item.remaining_units, item.wholesale_ppu) }}
              </td>

              <td :class="varianceClass(item)">
                {{ formatQty(item.stockCount, item.wholesale_ppu) }}
              </td>

              <td>
                <span
                  class="badge"
                  :class="item.remaining_units !== item.stockCount
                    ? 'badge-danger'
                    : 'badge-secondary'"
                >
                  {{ formatVariance(item) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ================= LIST VIEW ================= -->
    <div v-else>

      <div class="row align-items-center mb-3">
        <div class="col-md-6">
          <h4>Stock Variance Reports</h4>
        </div>

        <div class="col-md-6 text-right d-flex justify-content-end align-items-center">

          <!-- Auto Saved Toggle -->
          <button
            class="btn btn-sm btn-outline-secondary mr-2"
            @click="showAutoSaved = !showAutoSaved"
          >
            {{ showAutoSaved ? 'Hide Auto Saved' : 'Include Auto Saved' }}
          </button>

          <!-- Date Filter (UNCHANGED UX) -->
          <date-range-picker
            v-model="dateRange"
            :auto-apply="true"
            :opens="'left'"
            :append-to-body="true"
          >
            <template #input="picker">
              <span v-if="picker.startDate && picker.endDate">
                {{ formatDate(picker.startDate,'DD-MMM-YYYY') }}
                -
                {{ formatDate(picker.endDate,'DD-MMM-YYYY') }}
              </span>
              <span v-else class="text-muted">
                Filter by date
              </span>
            </template>
          </date-range-picker>
        </div>
      </div>

      <table
        v-if="displayedVarianceList.length"
        class="table table-sm table-striped"
      >
        <thead>
          <tr>
            <th>#</th>
            <th>Report</th>
            <th>Done By</th>
            <th>Date</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) in displayedVarianceList"
            :key="item.id"
            class="cursor-pointer"
            role="button"
            tabindex="0"
            @click="openReport(item.id)"
            @keyup.enter="openReport(item.id)"
          >
            <td>{{ index + 1 }}</td>
            <td>{{ item.variance_title }}</td>
            <td>{{ item.done_by?.name || 'System' }}</td>
            <td>{{ formatDate(item.created_at,'h:mm a DD-MM-YYYY') }}</td>
            <td>
              <span
                class="badge"
                :class="item.auto_save ? 'badge-secondary' : 'badge-primary'"
              >
                {{ item.auto_save ? 'Auto Saved' : 'Submitted' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="text-center py-5">
        <h5>No stock variance reports found</h5>
        <p class="text-muted">
          {{ formatDate(dateRange.startDate,'DD-MMM-YYYY') }}
          –
          {{ formatDate(dateRange.endDate,'DD-MMM-YYYY') }}
        </p>
        <a href="/stock-take" class="text-primary">
          Perform stock take
        </a>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'
import html2pdf from 'html2pdf.js'

export default {
  components: { DateRangePicker },

  data() {
    return {
      varianceList: [],
      varianceReport: null,
      applying: false,

      showAutoSaved: false,
      showAmountVariance: true,
      showAllItems: true,
      isHydrating: false,

      dateRange: {
        startDate: null,
        endDate: null,
      },
    }
  },

  computed: {
    // Applying a stock take changes stock: same rule as /api/adopt-inventory.
    canAdjustStock() {
      const user = this.$store.getters.userDetails || {}
      const roles = user.roles || []
      const perms = user.permissions || []
      return roles.some(r => r.name === 'Admin' || r.name === 'owner')
        || perms.some(p => p.name === 'can manage inventory' || p.name === 'adjust-stock')
    },
    displayedVarianceList() {
      return this.showAutoSaved
        ? this.varianceList
        : this.varianceList.filter(v => !v.auto_save)
    },

    displayedStock() {
      if (!this.varianceReport) return []
      return this.showAllItems
        ? this.varianceReport.current_stock
        : this.varianceReport.current_stock.filter(
            i => i.remaining_units !== i.stockCount
          )
    }
  },

  mounted() {
    const id = this.getQueryParam('v_id')
    this.fetchVariance(id)
  },

  watch: {
    dateRange: {
      deep: true,
      handler(after) {
        if (this.isHydrating) return
        if (!after?.startDate || !after?.endDate) return
        this.fetchVariance()
      }
    }
  },

  methods: {
    getQueryParam(key) {
      return new URLSearchParams(window.location.search).get(key)
    },

    openReport(id) {
      if (!id) return
      window.location.href = `/inventory-variance-report?v_id=${id}`
    },

    async fetchVariance(id = null) {
      this.isHydrating = true
      try {
        const params = {
          id,
          startdate: this.dateRange.startDate
            ? moment(this.dateRange.startDate).format('YYYY-MM-DD')
            : null,
          enddate: this.dateRange.endDate
            ? moment(this.dateRange.endDate).format('YYYY-MM-DD')
            : null,
        }

        const { data } = await axios.get('/api/inventory-variance', { params })

        if (Array.isArray(data.variance)) {
          this.varianceList = data.variance
          this.varianceReport = null
        } else {
          this.varianceReport = data.variance
          this.varianceList = []
        }
      } catch {
        this.$toasts.push({
          type: 'error',
          message: 'Failed to load inventory variance data'
        })
      } finally {
        this.isHydrating = false
      }
    },

    toggleVarianceView() {
      this.showAllItems = !this.showAllItems
    },

    varianceClass(item) {
      return item.remaining_units !== item.stockCount ? 'text-danger' : ''
    },

    formatQty(qty, ppu) {
    if (qty == null) return '-'
    return this.showAmountVariance
        ? `${qty} (${this.formatCurrency(qty * ppu)})`
        : `${qty}`
    },
    formatVariance(item) {
    let diff = item.remaining_units - item.stockCount

    // If diff is not an integer, force it to 0
    if (!Number.isInteger(diff)) {
        diff = 0
    }

    if (this.showAmountVariance) {
        return `${diff} (${this.formatCurrency(diff * item.wholesale_ppu)})`
    }

    return `${diff}`
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount)
    },

    formatDate(date, format) {
      if (!date) return '-'
      return moment(date).format(format)
    },

    async adoptAsInventory() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Stock will be set to these counts. This can only be done once for this stock take. Continue?',
        { title: 'Update Inventory', okVariant: 'danger' }
      )
      if (!ok) return
      this.applying = true
      try {
        await axios.post('/api/adopt-inventory', { variance_id: this.getQueryParam('v_id') })
        this.$set(this.varianceReport, 'applied_at', new Date().toISOString())
        this.$toasts.push({ type: 'success', message: 'Inventory updated successfully' })
      } catch (e) {
        this.$toasts.push({ type: 'error', message: e.response?.data?.message || 'Could not update inventory.' })
        if (e.response?.status === 422) this.$set(this.varianceReport, 'applied_at', this.varianceReport.applied_at || new Date().toISOString())
      } finally {
        this.applying = false
      }
    },

    generatePDF() {
      html2pdf()
        .from(this.$refs.varianceReportPdf)
        .set({ filename: 'variance_report.pdf' })
        .save()
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
tr.cursor-pointer:hover {
  background-color: #f5f7fa;
}
</style>
